import React, { memo, useEffect, useMemo, useState } from 'react'

import styles from './ScoreIndicator.module.scss'

export const SCORE_THRESHOLDS = {
  LOW: 40,
  HIGH: 90
}

type ScoreIndicatorProps = {
  value: number
  sizeInPx?: number
  strokeWidthInPx?: number
  fontSize?: number | string
  disableAnimation?: boolean
  transparent?: boolean
  colorsThresholds?: {
    LOW: number
    HIGH: number
  }
}

const ScoreIndicator: React.FC<ScoreIndicatorProps> = ({
  value,
  sizeInPx = 42,
  strokeWidthInPx = 7,
  fontSize,
  colorsThresholds = SCORE_THRESHOLDS,
  disableAnimation,
  transparent
}) => {
  const size = sizeInPx

  const strokeWidth = strokeWidthInPx

  const radius = (size - strokeWidth) / 2

  const circumference = radius * 2 * Math.PI

  const targetOffset = (circumference * (100 - value || 1)) / 100

  const [offset, setOffset] = useState(disableAnimation ? targetOffset : circumference)

  useEffect(() => {
    if (disableAnimation) {
      setOffset(targetOffset)
      return
    }

    requestAnimationFrame(() => {
      setOffset(targetOffset)
    })
  }, [disableAnimation, targetOffset])

  const strokeColor = useMemo(() => {
    if (value >= colorsThresholds.HIGH) {
      return '#5DDFB1'
    }

    if (value < colorsThresholds.LOW) {
      return '#F76988'
    }

    return '#ffdc4a'
  }, [value, colorsThresholds])

  const resolvedFontSize = useMemo(() => {
    if (fontSize) {
      return typeof fontSize === 'number' ? `${fontSize}px` : fontSize
    }

    return value === 100 ? '18px' : '24px'
  }, [fontSize, value])

  return (
    <div
      className={styles.container}
      style={{
        width: size + 3,
        height: size + 3
      }}
    >
      <svg className={styles.svg} width={size + 2} height={size + 2}>
        <circle
          r={radius}
          fill="none"
          cx={size / 2 + 1.1}
          cy={size / 2 + 0.8}
          stroke={'#ffffff'}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={`${circumference} ${circumference}`}
        />

        <circle
          fill="none"
          strokeWidth={1}
          r={radius * 1.24}
          cx={size / 2 + 1.1}
          cy={size / 2 + 0.8}
          stroke={'#ececec'}
          strokeLinecap="round"
          strokeDasharray={`${circumference * 2} ${circumference * 2}`}
        />

        <circle
          r={radius - 0.5}
          fill="none"
          cx={size / 2 + 1.1}
          cy={size / 2 + 0.8}
          stroke={strokeColor}
          strokeWidth={strokeWidth + 1.5}
          strokeLinecap="round"
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={offset}
          className={disableAnimation ? '' : styles.progress}
        />
      </svg>

      <div
        className={styles.innerContainer}
        style={{
          width: size + 2 - strokeWidth * 2,
          height: size + 2 - strokeWidth * 2,
          backgroundColor: transparent ? 'transparent' : '#f3f7ff'
        }}
      >
        <span
          className={styles.text}
          style={{
            fontSize: resolvedFontSize,
            color: strokeColor
          }}
        >
          {value}
        </span>
      </div>
    </div>
  )
}

export default memo(ScoreIndicator, (prevProps, nextProps) => prevProps.value === nextProps.value)
